import { useState } from 'react';
import { useFormDefinition } from '../../hooks/useFormDefinition';
import { leadsApi } from '../../api/leadsApi';
import { Button } from '../ui/Button';
import './DynamicForm.css';

/**
 * Construit et affiche un formulaire à partir de la définition renvoyée par /forms
 * (donc administrable depuis le Back Office sans toucher au code front).
 *
 * Exemple : <DynamicForm type="devis" onSuccess={() => setOpen(false)} />
 */
export function DynamicForm({ type, onSuccess }) {
  const { definition, loading } = useFormDefinition(type);
  const [values, setValues] = useState({});
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  function handleChange(name, value) {
    setValues((prev) => ({ ...prev, [name]: value }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setErrors({});
    setSubmitting(true);

    try {
      await leadsApi.submit(definition.id, values);
      setSent(true);
      onSuccess?.();
    } catch (err) {
      setErrors(err.errors || { _global: err.message });
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) return <p>Chargement du formulaire...</p>;
  if (!definition) return <p>Formulaire indisponible pour le moment.</p>;

  if (sent) {
    return <p className="dynamic-form__success">Merci, votre demande a bien été envoyée. Nous revenons vers vous rapidement.</p>;
  }

  return (
    <form className="dynamic-form" onSubmit={handleSubmit}>
      {definition.champs_json.map((champ) => (
        <div className="dynamic-form__field" key={champ.name}>
          <label htmlFor={champ.name}>
            {champ.label}
            {champ.required && ' *'}
          </label>

          {champ.type === 'textarea' ? (
            <textarea
              id={champ.name}
              required={champ.required}
              value={values[champ.name] || ''}
              onChange={(e) => handleChange(champ.name, e.target.value)}
              rows={4}
            />
          ) : champ.type === 'select' ? (
            <select
              id={champ.name}
              required={champ.required}
              value={values[champ.name] || ''}
              onChange={(e) => handleChange(champ.name, e.target.value)}
            >
              <option value="">Sélectionner...</option>
              {champ.options?.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          ) : (
            <input
              id={champ.name}
              type={champ.type}
              required={champ.required}
              value={values[champ.name] || ''}
              onChange={(e) => handleChange(champ.name, e.target.value)}
            />
          )}

          {errors[champ.name] && <span className="dynamic-form__error">{errors[champ.name]}</span>}
        </div>
      ))}

      {errors._global && <p className="dynamic-form__error">{errors._global}</p>}

      <Button type="submit" disabled={submitting}>
        {submitting ? 'Envoi...' : 'Envoyer'}
      </Button>
    </form>
  );
}
